import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";

export function useSlideLessonPageContextValue({
  initialPage = 0,
  isActive = true,
  onContinue,
  slideSlug,
}: {
  /** The view to open on (the one the learner was last on, when Undo returns to the slide). */
  initialPage?: number;
  isActive?: boolean;
  onContinue?: () => void;
  slideSlug: string;
}) {
  const [nextButtonLabelOption = "Next Card", setNextButtonLabelOption] = useState<string | null>(
    null,
  );
  // Custom footer content authored via `<SlideFooter>…</SlideFooter>`. When non-null it
  // replaces the default continue button in the sticky footer (see `slide-lesson-page-content`).
  const [slideFooterContent, setSlideFooterContent] = useState<ReactNode>(null);
  // The authored `<SlideTitle>` publishes its contents here so slides can
  // keep the title fixed between the profile and More controls instead of scrolling it in
  // the document body.
  const [slideTitleContent, setSlideTitleContent] = useState<ReactNode>(null);

  // The slide's views, its pages (see `@-/dev.improve.haniverse.lib.slide.pages`). The content
  // measures the slide and publishes how many it needs. The deck's swipe moves the top view on the
  // UI thread; Next and Undo/Redo turn views through `useGoToSlidePage`.
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const exits = useSharedValue<Readonly<Record<number, -1 | 1>>>({});
  const incoming = useSharedValue(initialPage);
  const leaving = useSharedValue<{ direction: -1 | 1; fromX: number; page: number } | null>(null);
  const leavingProgress = useSharedValue(0);
  const offsetX = useSharedValue(0);
  const shown = useSharedValue(initialPage);
  const width = useSharedValue(0);
  const views = useMemo(
    () => ({ exits, incoming, leaving, leavingProgress, offsetX, shown, width }),
    [exits, incoming, leaving, leavingProgress, offsetX, shown, width],
  );

  const nextButtonLabel = nextButtonLabelOption ?? "Next Card";

  return useMemo(
    () => ({
      currentPage,
      isActive,
      nextButtonLabel,
      onContinue,
      pageCount,
      setCurrentPage,
      setNextButtonLabelOption,
      setPageCount,
      setSlideFooterContent,
      setSlideTitleContent,
      slideFooterContent,
      slideSlug,
      slideTitleContent,
      views,
    }),
    [
      currentPage,
      isActive,
      nextButtonLabel,
      onContinue,
      pageCount,
      slideFooterContent,
      slideSlug,
      slideTitleContent,
      views,
    ],
  );
}

export type SlideLessonPageContextValue = ReturnType<typeof useSlideLessonPageContextValue>;
